import { Card } from "@attmos/flowstack-ui";
import { AlertTriangle, Clock, HelpCircle, Maximize2, Minimize2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * **The scientific-visual container (VIS/V0).** One frame that every
 * scientific visual in FlowStack drops into, whatever it is rendered with.
 *
 * The portal already hosts four rendering substrates — 3dmol on WebGL
 * (`viz/MoleculeViewer.tsx`), recharts on SVG (`affdo/DihedralProfilePlot.tsx`
 * and siblings), reactflow (`dag/DAGCanvas.tsx`) and plain DOM
 * (`affdo/FragmentSummaryTable.tsx`) — and before this file each had grown its
 * own header, its own inset, its own empty state and its own expand
 * behaviour. That is affordable at two visuals and is a fresh design argument
 * at every one after: a form decided panel-by-panel is re-decided for every
 * new visual type, and FlowStack is going to host many.
 *
 * So the form is decided ONCE, here, and the next visual inherits it.
 *
 * ---
 *
 * ## THE CONTAINER NEVER COLOURS THE CONTENT
 *
 * **This is a rule, not a convention, and it is why the container exists at
 * all.** Every scientific visual carries colour semantics that are not ours to
 * choose: CPK atom colours are fixed by convention (carbon grey, oxygen red,
 * nitrogen blue), a heatmap carries a sequential or diverging scale, the DAG
 * carries run state, and the torsion charts carry six CVD-validated series
 * (`--chart-series-1..6`). The surround has to recede so each of those reads
 * correctly.
 *
 * Concretely, and these are the lines to hold when editing this file:
 *
 * - the container paints only the neutral ladder — `--card`, `--surface-2` and
 *   `--surface-3` on the maximise control, `--border-subtle`,
 *   `--muted-foreground`/`--foreground`, and the app's focus `--ring`. The
 *   list is written out rather than abbreviated to three tokens because the
 *   sentence beside it is the container's central claim, and a claim checked
 *   mechanically should name what it actually paints. It sets **no hue**; the body
 *   carries no colour of its own beyond the neutral ink a card already
 *   inherits, so nothing it does can reach into a child's encoding;
 * - it never tints its ground. A tinted ground is not a taste question here:
 *   simultaneous contrast shifts how a viewer judges a colour placed on it, so
 *   a warm ground changes how a heatmap cell or an atom colour is read, and
 *   for a tool whose claim is auditability that is a correctness problem;
 * - accent colour is a scarce resource spent on DATA. Every hue the chrome
 *   claims is one the data cannot safely use.
 *
 * The ONE place a state hue is allowed is {@link VisualPanelNotice}'s `error`
 * mark, where the failure is the content — and even there `unknown` stays
 * neutral, because an unknown is not a failure and borrowing the run-state
 * palette for it would assert one.
 *
 * That rule is checked mechanically by the no-hue scan in
 * `VisualPanel.test.tsx`, and the scan needs to know which nodes are the
 * container's own and which are an adopter's — a status chip handed to
 * `controls` is SUPPOSED to carry hue. The `data-visual-panel-slot` markers on
 * the header's two clusters and on the caption are what make that scoping
 * mechanical: the scan honours a `data-adopter` exemption only INSIDE one of
 * them. A marker on the panel root, or on anything above it, therefore exempts
 * nothing — an exclusion an ancestor could switch off is a guard that reads as
 * proof while proving less (flowstack#1200). `data-adopter` is a
 * **test-harness marker only**; it has no meaning in production markup and
 * writing one there is a way to weaken the check, never to configure it.
 *
 * ## The form (VIS direction B, existing palette)
 *
 * B's FORM was adopted and B's teal was not (EPIC-003 rev 13). Applied here,
 * once, rather than per panel:
 *
 * - **14px corner** — `--visual-panel-radius`, a container-scale radius above
 *   the app's 8px `--radius` control corner;
 * - **the larger inset** — `p-3` on the body rather than the `p-2` the run
 *   workspace's main pane carried. Deliberately NOT `p-card`: `globals.css`
 *   reserves `--card-pad` for a card that holds CONTENT, and says a card that
 *   only frames a self-padded child — "a table, a canvas, a scroll list of
 *   padded rows" — keeps the hairline inset. A visual container is that case
 *   by definition, and putting 14px around a canvas that already fills its box
 *   would be inset applied to nothing;
 * - **the softer border and the calm elevation were ALREADY what we ship** —
 *   `Card`'s base is `border-border-subtle`, and its elevation defaults to
 *   `flat` because 36 of the app's 55 card surfaces render no shadow (U1.2).
 *   So neither changes here. Elevation stays unspent on purpose: a lift drawn
 *   around a figure is ink competing with the figure.
 *
 * ## Full-window is part of the contract, not a feature of one adopter
 *
 * A container that cannot go full-window is not a candidate — reading a
 * fifteen-node graph, a dihedral grid or a structure in a column is the
 * problem VIZ U5.3 was cut to fix. So the maximise CONTROL lives on the panel
 * and the FRAME lives in {@link VisualPanelFrame}, which ships beside it. An
 * adopter wires the two together and inherits, unchanged, the four guarantees
 * U5.3 (`38b286d2`) landed and that a reshaping is exactly where a working
 * capability gets quietly dropped:
 *
 * 1. **the frame is `fixed inset-0 z-50`** — the whole viewport, never the
 *    column ({@link VisualPanelFrame});
 * 2. **Escape exits**, owned by the frame rather than by any visual inside it
 *    (#991: it used to live in `DAGCanvas`, which a mode switch can unmount,
 *    so the way out vanished exactly when a user was deepest in a full-screen
 *    view) — and it fires for any Escape that reaches `window`, so a nested
 *    dismissable layer that wants to keep the frame up must `stopPropagation`
 *    (see {@link VisualPanelFrame});
 * 3. **`min-h-0` on every flex child of an expanded path** (#982) — a flex
 *    child defaults to `min-height: auto` and refuses to shrink below its
 *    content, so without it a maximised pane overflows the DOCUMENT instead of
 *    scrolling inside the frame. `maximised` puts it on the panel and on its
 *    body; the adopter still owns the elements between them;
 * 4. **a stood-down sibling is HIDDEN, never unmounted** — `standDown` is
 *    `display: none`, which keeps React state, react-query subscriptions and
 *    (the case that matters) a live WebGL context. Conditional rendering would
 *    be simpler and is the bug.
 */

/**
 * The elements a visual panel may be.
 *
 * A closed union, per this repo's "closed vocabularies are `Literal`, never
 * free `str`" rule, and narrower than `Card`'s own: a visual container is
 * either the thing you came to look at (`section`) or the detail beside it
 * (`aside`). It is never a `dl` or a `fieldset`.
 */
export type VisualPanelElement = "section" | "aside";

export interface VisualPanelProps {
  /** Which landmark this panel is — the subject, or the detail beside it. */
  as: VisualPanelElement;
  /**
   * The panel element's own accessible name. Required: an unnamed landmark is
   * one a screen-reader user has to enter to identify.
   */
  label: string;
  /**
   * The header's visible heading. Omit it when {@link titleControl} takes the
   * slot instead — a control that names the pane AND changes it (the run
   * workspace's mode switch) is the heading, and a second one beside it would
   * say the same thing twice.
   */
  title?: string;
  /** A control occupying the heading's slot. Mutually exclusive with `title`. */
  titleControl?: React.ReactNode;
  /**
   * The IDENTIFIER slot: what this panel is showing, in mono — a content
   * hash, a flow name, a fragment id. Rendered beside the heading, truncated,
   * never wrapped. Omit it rather than passing a placeholder; an em-dash in an
   * identifier slot is indistinguishable from an identifier of "—".
   *
   * Pass a **string** where you can: the slot then carries the full value in
   * its `title`, so a clipped identifier stays recoverable rather than lost.
   */
  identifier?: React.ReactNode;
  /** The right-side control cluster. The maximise control is appended to it. */
  controls?: React.ReactNode;
  /**
   * The CAPTION / PROVENANCE slot, under the body and pinned below an
   * internal scroll.
   *
   * This is the slot that earns the container. `U5.2a`/`U5.2b` put provenance
   * on scan points, and the same need recurs for every visual that shows a
   * computed value: where did this come from, and is the number beside it
   * describing the same thing. Giving it a home stops the next visual
   * inventing one.
   *
   * Fill it only where there is real provenance to state. An empty caption
   * slot is honest; a decorative one is a claim.
   */
  caption?: React.ReactNode;
  /** THIS panel holds the frame — never merely "something is maximised". */
  maximised?: boolean;
  /**
   * Toggle the frame. Omitted entirely for a panel that does not maximise, in
   * which case no control renders (rather than a disabled one that reads as
   * broken).
   */
  onToggleMaximise?: () => void;
  /**
   * What the maximise control calls this pane — "Expand the {paneName}".
   * Defaults to {@link label}. The run workspace's aside needs both, because
   * the landmark is "Torsion fits panel" while the control has to say "side
   * panel" to distinguish it from the main pane's.
   */
  paneName?: string;
  /**
   * This panel's sibling has the frame, so this one stands down: `hidden`
   * (`display: none`), NOT unmounted. See guarantee 4 in the file docstring.
   */
  standDown?: boolean;
  /**
   * Whether the body scrolls internally while maximised. `false` fills the
   * box instead — a scrollbar on a pannable canvas or a 3D viewer is a trap,
   * while a list of plots can exceed any frame.
   */
  scrolls?: boolean;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}

/** The container. One per scientific visual; see the file docstring. */
export function VisualPanel({
  as,
  label,
  title,
  titleControl,
  identifier,
  controls,
  caption,
  maximised = false,
  onToggleMaximise,
  paneName,
  standDown = false,
  scrolls = false,
  className,
  bodyClassName,
  children,
}: VisualPanelProps): JSX.Element {
  return (
    <Card
      as={as}
      aria-label={label}
      className={cn(
        // `rounded-[var(…)]` rather than a named `rounded-panel` utility, and
        // it is not a style preference: `cn()` is `twMerge`, which recognises
        // an ARBITRARY value as a member of the `rounded` group and therefore
        // lets it beat `Card`'s own `rounded-lg`. A custom scale key does not
        // parse as one, so both classes would survive the merge and the corner
        // would be decided by whichever Tailwind emitted last. Same escape
        // hatch `globals.css` documents for `shadow-[var(--elevation-*)]`.
        "flex flex-col overflow-hidden rounded-[var(--visual-panel-radius)]",
        maximised && "min-h-0",
        standDown && "hidden",
        className
      )}
    >
      <div
        data-visual-panel-head=""
        // `shrink-0` is what pins the header while the body scrolls under it —
        // the thing that used to scroll off the top of an expanded pane.
        // `panel-head` is the hairline's wash (`globals.css`).
        className="panel-head flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border-subtle px-3 py-2"
      >
        <div data-visual-panel-slot="heading" className="flex min-w-0 items-center gap-2">
          {titleControl ??
            (title !== undefined && (
              <h2 className="label-micro truncate text-muted-foreground">{title}</h2>
            ))}
          {identifier !== undefined && identifier !== null && (
            // `min-w-0` is what makes the `truncate` beside it work at all
            // (flowstack#991): a flex item's `min-width` defaults to `auto`, so
            // without it the span refuses to go below its content width and
            // `overflow: hidden` never has anything to hide. Everything this
            // slot holds is an unbreakable token — a content hash, a fragment
            // id, `tor-0 · affdo.torsion_scan` — and the rail it sits in can
            // now be dragged down to 240px, so the same overflow that ran a
            // `fail_reason` across its neighbours was one drag away here.
            //
            // `title` only for a plain string: the slot takes a `ReactNode`,
            // and `String(<node>)` would put "[object Object]" on the tooltip
            // of the one case that most needs a real one.
            <span
              data-visual-panel-identifier=""
              title={typeof identifier === "string" ? identifier : undefined}
              className="min-w-0 truncate font-mono text-[11px] text-muted-foreground"
            >
              {identifier}
            </span>
          )}
        </div>
        <div data-visual-panel-slot="controls" className="flex items-center gap-1.5">
          {controls}
          {onToggleMaximise !== undefined && (
            <MaximiseButton
              paneName={paneName ?? label}
              maximised={maximised}
              onToggle={onToggleMaximise}
            />
          )}
        </div>
      </div>

      <div
        data-visual-panel-body=""
        className={cn(
          "flex flex-col p-3",
          maximised && "min-h-0 flex-1",
          maximised && (scrolls ? "overflow-y-auto" : "overflow-hidden"),
          bodyClassName
        )}
      >
        {children}
      </div>

      {caption !== undefined && caption !== null && (
        <div
          data-visual-panel-caption=""
          data-visual-panel-slot="caption"
          // `shrink-0` for the same reason the header carries it: provenance
          // that scrolls away under a maximised plot is provenance the reader
          // has to go looking for.
          className="shrink-0 border-t border-border-subtle px-3 py-1.5 text-[11px] leading-snug text-muted-foreground"
        >
          {caption}
        </div>
      )}
    </Card>
  );
}

/**
 * A pane's own maximise control.
 *
 * ONE component for every panel so no two can drift into different chrome or
 * different wording, and so the accessible name is always built the same way:
 * the verb plus the pane's name. "Expand" alone is unambiguous while there is
 * one frame on the page; with two it is a control the user has to click to
 * find out what it does.
 *
 * The glyph is `aria-hidden`, so the accessible name is exactly the verb and
 * the pane name — the icon is a second cue, never the only one.
 */
function MaximiseButton({
  paneName,
  maximised,
  onToggle,
}: {
  paneName: string;
  maximised: boolean;
  onToggle: () => void;
}): JSX.Element {
  const label = maximised ? `Exit the expanded ${paneName}` : `Expand the ${paneName}`;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={maximised ? `${label} (Esc)` : label}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border-subtle bg-surface-2 text-muted-foreground ring-offset-background transition-colors hover:bg-surface-3 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {maximised ? (
        <Minimize2 className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <Maximize2 className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  );
}

/**
 * The full-window frame a {@link VisualPanel} maximises into.
 *
 * It ships with the container rather than being rebuilt per adopter, because
 * the four things it does are exactly the four a reshaping drops by accident
 * (see the file docstring). It is a separate component from the panel because
 * ONE frame can hold one of SEVERAL panels — the run workspace has two — so
 * the frame is the adopter's, and which panel is in it is the adopter's state.
 *
 * `active` is "some panel of mine is maximised". While it is false this
 * renders an ordinary flex column, adds no listeners, and touches nothing.
 */
export function VisualPanelFrame({
  active,
  label,
  onExit,
  className,
  children,
}: {
  active: boolean;
  /**
   * The frame's accessible name while it is up. Say WHICH panel is expanded,
   * not merely that something is: "workspace, expanded" left a screen-reader
   * user unable to tell what they had just entered.
   */
  label: string;
  onExit: () => void;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  // The frame covers the page but the page keeps its scrollbar, and a wheel
  // event the content does not take would scroll the document behind it.
  //
  // It RESTORES the prior value rather than clearing to `""`, and the
  // difference is only visible on the non-empty path: the page can already be
  // locked when the frame goes up — a Radix dialog holding its own lock, or a
  // second frame above this one — and clearing would hand the page back its
  // scroll while that other layer still expects it held. Restoring means the
  // outermost lock wins, which is the only ordering that composes.
  React.useEffect(() => {
    if (!active) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);

  // Escape exits — owned HERE, at the frame, and that placement is the fix
  // #991 landed. A handler inside a visual works only while that visual is
  // mounted, so with a mode switch above it the way out silently stops
  // existing in the modes that unmount it. The frame is the one element that
  // exists in every mode.
  //
  // **The nested-dismissable rule**, because a maximised panel may hold a
  // popover, a select or a dialog of its own: the listener is on `window`, in
  // the BUBBLE phase, so it fires for every Escape that is not stopped on the
  // way up. A nested layer that means to keep the frame open must call
  // `stopPropagation()` on the keydown — `preventDefault()` is NOT enough, and
  // Radix's dismissable layers do not stop propagation for Escape, so by
  // default one Escape both dismisses the layer and exits the frame.
  //
  // That default is deliberate rather than tolerated. The frame's Escape is the
  // documented way out of a `fixed inset-0` surface, and a layer that swallows
  // it by merely existing is exactly how the way out went missing in #991 — so
  // a layer that owns Escape has to say so, and the cost of not saying so is an
  // extra dismissal rather than a trapped user.
  React.useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      onExit();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active, onExit]);

  return (
    <div
      className={cn(
        "flex flex-col",
        // `flex-col` here plus `min-h-0 flex-1` on the adopter's row is what
        // confines the maximised panel to the viewport instead of letting it
        // grow the page.
        active && "fixed inset-0 z-50 bg-background p-3",
        className
      )}
      role={active ? "region" : undefined}
      aria-label={active ? label : undefined}
    >
      {children}
    </div>
  );
}

/**
 * The four states a visual can be in instead of rendering, and they are FOUR
 * because collapsing any of them into another is `flowstack#923`.
 *
 * - `empty` — there is nothing to show, and that is a legitimate outcome. A
 *   force-field bundle, a topology or a log has no visual.
 * - `pending` — it does not exist YET. During a live run this is the normal
 *   state for most visuals.
 * - `error` — it exists and could not be loaded. A failure, and the only
 *   status that borrows a run-state hue, because here the failure IS the
 *   content.
 * - `unknown` — the value arrived but its meaning cannot be established: a
 *   provenance field that is absent, or one carrying a token this build does
 *   not recognise. **An unknown is not an error and is not an absence.** It is
 *   the state `U5.2a`/`U5.2b` had to keep distinct so an unverified geometry
 *   could never render as an optimised one.
 */
export type VisualPanelStatus = "empty" | "pending" | "error" | "unknown";

/**
 * How each status is DRAWN — the table that makes "an error must never read as
 * *no data*" mechanical rather than a thing each surface remembers.
 *
 * A `Record` over the union, so a fifth status added without deciding its mark
 * is a type error rather than a silent fall-through to whichever branch came
 * last. Every status differs from every other in at least two channels — the
 * ground (dashed = nothing here; solid = something is here and this is what we
 * know about it) and the glyph — before a word of copy is read:
 *
 * | status  | ground        | glyph        | hue          |
 * |---------|---------------|--------------|--------------|
 * | empty   | dashed        | none         | neutral      |
 * | pending | dashed        | clock        | neutral      |
 * | error   | solid         | triangle     | failed-system|
 * | unknown | solid         | question     | neutral      |
 *
 * `empty` and `pending` share the dashed ground deliberately: both mean "there
 * is no visual here", and the honest difference between them is whether one is
 * still coming — which is a claim about the future and belongs in the copy.
 * `error` and `unknown` are on the solid ground precisely so neither can be
 * mistaken for that pair.
 */
const STATUS_MARK: Record<
  VisualPanelStatus,
  { ground: string; icon: React.ComponentType<{ className?: string }> | null }
> = {
  empty: {
    ground: "border-dashed border-border-subtle bg-surface-2/50 text-muted-foreground",
    icon: null,
  },
  pending: {
    ground: "border-dashed border-border-subtle bg-surface-2/50 text-muted-foreground",
    icon: Clock,
  },
  error: {
    ground:
      "border-state-failed-system-border bg-state-failed-system-bg text-state-failed-system-fg",
    icon: AlertTriangle,
  },
  unknown: {
    // Neutral ON PURPOSE. An unknown is not a run state, and dressing it in
    // the run-state palette would assert a severity the data does not carry.
    // It is distinguished from `empty` by its SOLID ground and its glyph.
    ground: "border-border-subtle bg-surface-2 text-muted-foreground",
    icon: HelpCircle,
  },
};

export interface VisualPanelNoticeProps {
  status: VisualPanelStatus;
  /** Short and declarative — "This run produced no torsion plots." */
  title: string;
  /** Why the surface is in this state, and the next action where one exists. */
  description?: string;
  className?: string;
}

/**
 * What a {@link VisualPanel} renders in place of its visual.
 *
 * Every adopter uses this rather than its own box, which is the whole point:
 * the four states cost one prop instead of four hand-written empty states that
 * drift apart, and the one that drifts first is always the one that turns an
 * error into "no data".
 *
 * Voice per the GUI plan: calm, competent, scientifically literate. The title
 * states the fact; the description says why and what to do about it.
 */
export function VisualPanelNotice({
  status,
  title,
  description,
  className,
}: VisualPanelNoticeProps): JSX.Element {
  const { ground, icon: Icon } = STATUS_MARK[status];
  return (
    <div
      // `role="status"` rather than an alert: these are states of a panel a
      // user navigated to, not interruptions. `data-status` is the hook the
      // adoption tests read, so "an error does not render as an empty" is
      // asserted on the status this component actually drew rather than on a
      // sentence somebody could reword.
      role="status"
      data-visual-panel-status={status}
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-md border px-6 py-10 text-center",
        ground,
        className
      )}
    >
      {Icon !== null && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
      <p className="text-sm font-semibold">{title}</p>
      {/* The description INHERITS the ground's ink rather than naming
          `--muted-foreground`: on the error ground that token is a neutral
          grey over a red-tinted surface, which is the one combination in this
          component that would be hard to read. Weight carries the hierarchy
          instead of colour. */}
      {description !== undefined && <p className="max-w-sm text-xs opacity-90">{description}</p>}
    </div>
  );
}
